/*
 * Inventory PDF helpers — jsPDF-powered.
 *
 * Two documents:
 *   • Purchase Order (HQ → Supplier) — reorder list to email a supplier.
 *   • Pick Slip (HQ → Team)          — signed slip a team hands to HQ when
 *     collecting an approved stock request.
 */
import jsPDF from "jspdf";

const NAVY = "#0F2857";
const TEAL = "#2A9F92";

// Detect iOS/iPadOS — on those platforms the `<a download>` attribute is
// ignored, so `doc.save()` navigates the current tab to the blob URL and
// the browser Back button lands on the site's landing page instead of the
// inventory screen. Opening the PDF in a new tab keeps the /inventory tab
// intact so Back works as expected.
function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isTouchMac = /Mac/.test(ua) && (navigator.maxTouchPoints || 0) > 1;
  return /iPad|iPhone|iPod/.test(ua) || isTouchMac;
}

function savePdf(doc, filename) {
  if (isIOS()) {
    // Open in a fresh tab so the current /inventory history entry stays.
    const url = doc.output("bloburl");
    const win = window.open(url, "_blank");
    if (!win) {
      // Popup blocked — fall back to the default save (last resort).
      doc.save(filename);
    }
    return;
  }
  doc.save(filename);
}

function newDoc() {
  return new jsPDF({ unit: "pt", format: "letter" });
}

function header(doc, title, subtitle) {
  doc.setFillColor(NAVY);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 72, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("HepCURE", 40, 42);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("Inventory Operations", 40, 58);

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(title, doc.internal.pageSize.getWidth() - 40, 42, { align: "right" });
  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(subtitle, doc.internal.pageSize.getWidth() - 40, 58, { align: "right" });
  }
  doc.setTextColor(20, 20, 20);
}

function twoCol(doc, x, y, left, right) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(90, 90, 90);
  doc.text(left.label.toUpperCase(), x, y);
  doc.text(right.label.toUpperCase(), x + 260, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(20, 20, 20);
  doc.text(left.value || "—", x, y + 14);
  doc.text(right.value || "—", x + 260, y + 14);
}

function table(doc, y, columns, rows) {
  const pageW = doc.internal.pageSize.getWidth();
  const marginX = 40;
  const usableW = pageW - marginX * 2;
  const totalWeight = columns.reduce((s, c) => s + (c.w || 1), 0);
  const widths = columns.map((c) => (usableW * (c.w || 1)) / totalWeight);
  // Column key that, if present on a row, renders as a wrapped sub-line
  // (smaller, italic) directly under the main row — used for the PO's
  // per-item Description text.
  const descCol = columns.find((c) => c.desc);
  const descColIdx = descCol ? columns.indexOf(descCol) : -1;
  const descWidth = descColIdx >= 0 ? widths[descColIdx] - 12 : 0;

  // Header row
  doc.setFillColor(240, 245, 244);
  doc.rect(marginX, y, usableW, 22, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  let x = marginX + 8;
  columns.forEach((c, i) => {
    doc.text(c.label.toUpperCase(), x, y + 15);
    x += widths[i];
  });

  // Body rows
  doc.setFontSize(10);
  doc.setTextColor(20, 20, 20);
  let yy = y + 22;
  rows.forEach((row, idx) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const descText = descCol ? String(row[descCol.desc] || "").trim() : "";
    const descLines = descText
      ? doc.splitTextToSize(descText, descWidth || usableW)
      : [];
    // Wrap every cell to its column width so long values (e.g. supplier
    // ordering codes like GXHCV-VL-FS-CA-10) never bleed into the next
    // column. Row height grows to fit the tallest wrapped cell.
    const cellLines = columns.map((c, i) =>
      doc.splitTextToSize(String(row[c.key] ?? ""), Math.max(widths[i] - 12, 20))
    );
    const maxCellLines = Math.max(...cellLines.map((l) => l.length), 1);
    const rowH = 22 + (maxCellLines - 1) * 12 + (descLines.length ? 12 * descLines.length + 4 : 0);
    if (idx % 2 === 0) {
      doc.setFillColor(252, 252, 252);
      doc.rect(marginX, yy, usableW, rowH, "F");
    }
    let xx = marginX + 8;
    columns.forEach((c, i) => {
      doc.text(cellLines[i], xx, yy + 15);
      xx += widths[i];
    });
    // Description sub-line under the row (aligned to the descCol start).
    if (descLines.length && descColIdx >= 0) {
      const descX = marginX + 8 + widths.slice(0, descColIdx).reduce((s, w) => s + w, 0);
      doc.setFont("helvetica", "italic");
      doc.setFontSize(9);
      doc.setTextColor(90, 90, 90);
      descLines.forEach((ln, i) => doc.text(ln, descX, yy + 15 + 14 + i * 12));
      doc.setFontSize(10);
      doc.setTextColor(20, 20, 20);
    }
    yy += rowH;
  });

  // Border
  doc.setDrawColor(220, 220, 220);
  doc.rect(marginX, y, usableW, yy - y);
  return yy;
}

function footer(doc, text) {
  const pageH = doc.internal.pageSize.getHeight();
  doc.setDrawColor(230, 230, 230);
  doc.line(40, pageH - 60, doc.internal.pageSize.getWidth() - 40, pageH - 60);
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text(text, 40, pageH - 42);
}

// ─────────────────────────────────────────────────────────────────
// Purchase Order — HQ → Supplier
//
// `buildPurchaseOrderPDF` returns `{ doc, filename, po }` so callers can
// preview it in an iframe before deciding to save. `downloadPurchaseOrderPDF`
// is the thin wrapper that saves immediately (kept for legacy callers).
// ─────────────────────────────────────────────────────────────────
export function buildPurchaseOrderPDF({ supplier, rows, poNumber }) {
  const doc = newDoc();
  const today = new Date();
  const dateStr = today.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  const po = poNumber || `PO-${today.getFullYear()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;

  const acctSuffix = supplier?.account_number ? ` · Account # ${supplier.account_number}` : "";
  header(doc, "PURCHASE ORDER", `${po} · ${dateStr}${acctSuffix}`);

  // LEFT column — Supplier block, laid out as a stacked address block
  // (matches the HepCURE ship-to block on the right for visual balance).
  const leftX = 40;
  const rightX = 300;
  const blockTopY = 100;

  // Section labels (SUPPLIER / SHIP TO) — same y, both sides.
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(90, 90, 90);
  doc.text("SUPPLIER", leftX, blockTopY);
  doc.text("SHIP TO", rightX, blockTopY);

  // Primary line: Company / HepCURE.
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(20, 20, 20);
  doc.text(supplier?.company || "—", leftX, blockTopY + 14);
  doc.text("HepCURE", rightX, blockTopY + 14);

  // Sub-label: CONTACT / ATTN (aligned to same baseline).
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(90, 90, 90);
  doc.text("CONTACT", leftX, blockTopY + 40);
  doc.text("ATTN", rightX, blockTopY + 40);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(20, 20, 20);
  const contactName = [supplier?.first, supplier?.last].filter(Boolean).join(" ") || "—";
  doc.text(contactName, leftX, blockTopY + 54);
  doc.text("Receiving Team", rightX, blockTopY + 54);

  // Address / phone / email — stacked as a single flowing block on both
  // sides so the supplier column mirrors the HepCURE ship-to block.
  // Prefer structured city/province/postal fields, fall back to legacy
  // free-form `address` string (split on newlines or commas).
  const splitAddress = (raw) => {
    if (!raw) return [];
    const parts = raw.includes("\n") ? raw.split(/\n+/) : raw.split(",");
    return parts.map((s) => s.trim()).filter(Boolean);
  };
  const supplierLines = [];
  if (supplier?.city || supplier?.province || supplier?.postal) {
    // Structured: street on its own line, then "City, Province  Postal".
    if (supplier?.address) supplierLines.push(supplier.address);
    const cityLine = [supplier?.city, [supplier?.province, supplier?.postal].filter(Boolean).join("  ")].filter(Boolean).join(", ");
    if (cityLine) supplierLines.push(cityLine);
  } else {
    splitAddress(supplier?.address).forEach((ln) => supplierLines.push(ln));
  }
  supplierLines.push("");
  if (supplier?.tel)   supplierLines.push(`Tel: ${supplier.tel}`);
  if (supplier?.email) supplierLines.push(`Email: ${supplier.email}`);

  const hepcureLines = [
    "530 Adelaide Street West",
    "Unit # 4406",
    "Toronto, ON  M5V 1T5",
    "",
    "Tel: 1-844-925-2873",
    "Email: finance@hepcure.ca",
  ];

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(70, 70, 70);
  const stackTopY = blockTopY + 74;
  supplierLines.forEach((ln, i) => doc.text(ln, leftX, stackTopY + i * 12));
  hepcureLines.forEach((ln, i)   => doc.text(ln, rightX, stackTopY + i * 12));

  // Reorder table
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(NAVY);
  doc.text("Items requested", 40, 300);

  const body = rows.map((r) => {
    const per = Number(r.packQty || 1);
    // orderQty is in PACKS. Fallback (when caller didn't pre-compute):
    // hqMax (packs) - packsOnHand (floor(q/packQty)).
    const packs = Number(
      r.orderQty ?? Math.max((r.hqMax || 0) - Math.floor((r.q || 0) / per), 0)
    ) || 0;
    return {
      code: r.code || "—",
      name: r.name,
      description: (r.description || "").trim(),
      qty:  String(packs),
      pack: String(per),
      total: String(packs * per),
    };
  });

  const yEnd = table(doc, 312,
    [
      { key: "code",   label: "Code",  w: 1.7 },
      { key: "name",   label: "Item",  w: 3.1, desc: "description" },
      { key: "qty",    label: "Qty",   w: 1.0 },
      { key: "total",  label: "Total", w: 1.2 },
    ],
    body,
  );

  // Special instructions
  if (supplier?.instructions) {
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.setFont("helvetica", "bold");
    doc.text("Special instructions", 40, yEnd + 30);
    doc.setFont("helvetica", "normal");
    const lines = doc.splitTextToSize(supplier.instructions, doc.internal.pageSize.getWidth() - 80);
    doc.text(lines, 40, yEnd + 46);
  }

  footer(doc, `HepCURE Inventory · Purchase Order ${po}`);

  const safe = (supplier?.company || "supplier").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  return { doc, filename: `${po}-${safe}.pdf`, po };
}

// Legacy wrapper that saves immediately.
export function downloadPurchaseOrderPDF(payload) {
  const { doc, filename, po } = buildPurchaseOrderPDF(payload);
  savePdf(doc, filename);
  return po;
}

// ─────────────────────────────────────────────────────────────────
// Pick Slip — HQ → Team (issued when a request is approved)
// Supports BOTH single-item calls (legacy: {item, qty, ...}) AND
// batched multi-item calls ({items: [{item, qty, requestId?, reason?}]}).
// The batched shape is used when several requests for the same team are
// approved on the same day and released together on one slip.
//
// `buildPickSlipPDF` returns `{ doc, filename, slipNo }`; the download
// wrapper below simply saves it.
// ─────────────────────────────────────────────────────────────────
export function buildPickSlipPDF(payload) {
  const {
    team, teamLabel,
    approvedBy, dateLabel,
  } = payload || {};

  // Normalize single-item legacy shape → items[] array.
  const items = Array.isArray(payload?.items) && payload.items.length
    ? payload.items
    : [{
        item: payload?.item,
        qty: payload?.qty,
        requestId: payload?.requestId,
        reason: payload?.reason,
      }];

  const doc = newDoc();
  const today = new Date();
  const dateStr = dateLabel || today.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  // Slip number: use the first request ID's tail as the shared reference.
  const firstReqId = items[0]?.requestId || "";
  const slipNo = `PICK-${firstReqId.replace(/[^A-Z0-9]/gi, "").slice(-6).toUpperCase() || Math.random().toString(36).slice(2, 6).toUpperCase()}`;

  header(doc, "STOCK PICK SLIP", `${slipNo} · ${dateStr}`);

  twoCol(doc, 40, 100,
    { label: "Team",         value: teamLabel || team || "—" },
    { label: "Approved by",  value: "HQ" },
  );
  twoCol(doc, 40, 140,
    { label: "Requests",     value: items.length === 1 ? (items[0].requestId || "—") : `${items.length} items` },
    { label: "Status",       value: "READY FOR PICKUP" },
  );

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(NAVY);
  doc.text(items.length === 1 ? "Item to release from HQ" : "Items to release from HQ", 40, 190);

  const body = items.map((row) => {
    const packs = Number(row?.qty ?? 0);
    const per = Number(row?.item?.packQty || 1);
    return {
      code: row?.item?.code || "—",
      name: row?.item?.name || "—",
      qty:  String(packs),
      pack: String(per),
      total: String(packs * per),
    };
  });

  const yEnd = table(doc, 204,
    [
      { key: "code",   label: "Code",  w: 1.7 },
      { key: "name",   label: "Item",  w: 2.9 },
      { key: "qty",    label: "Qty",   w: 1.0 },
      { key: "total",  label: "Total", w: 1.0 },
    ],
    body,
  );

  // Reasons — only if any item has one. Skip auto-generated reorder
  // notes (e.g. "Auto — Aug 2026 end count below min (0 < 100)") which
  // add noise for HQ pickers who only need the qty. Skip for the
  // batched all-blank case too.
  const AUTO_RE = /^\s*Auto\b/i;
  const reasons = items
    .map((r) => r.reason)
    .filter((t) => t && !AUTO_RE.test(t));
  if (reasons.length) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.text("Notes", 40, yEnd + 24);
    doc.setFont("helvetica", "normal");
    const lines = reasons.map((t, i) => `${i + 1}. ${t}`);
    doc.text(lines, 40, yEnd + 40);
  }

  footer(doc, `HepCURE Inventory · Stock Pick Slip ${slipNo}`);
  const safe = (teamLabel || team || "team").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  return { doc, filename: `${slipNo}-${safe}.pdf`, slipNo };
}

// Legacy wrapper — save immediately.
export function downloadPickSlipPDF(payload) {
  const { doc, filename, slipNo } = buildPickSlipPDF(payload);
  savePdf(doc, filename);
  return slipNo;
}
